// Lightweight live regions for screen-reader announcements.
// Mounts one visually-hidden node per politeness level in <body> and
// toggles its text so assistive tech re-announces even the same string.

type Politeness = "polite" | "assertive";

const nodes: Partial<Record<Politeness, HTMLDivElement>> = {};
const clearTimers: Partial<Record<Politeness, number>> = {};
const setTimers: Partial<Record<Politeness, number>> = {};

const HIDDEN =
  "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0";

function ensureNode(level: Politeness): HTMLDivElement | null {
  if (typeof document === "undefined") return null;
  const existing = nodes[level];
  if (existing && document.body.contains(existing)) return existing;
  const el = document.createElement("div");
  el.setAttribute("role", level === "assertive" ? "alert" : "status");
  el.setAttribute("aria-live", level);
  el.setAttribute("aria-atomic", "true");
  el.style.cssText = HIDDEN;
  document.body.appendChild(el);
  nodes[level] = el;
  return el;
}

/**
 * Speak `message` to assistive tech.
 *
 * `assertive` interrupts — reserve it for state the user just caused and must
 * hear immediately (picked up a photo, dropped it, cover changed). Everything
 * ambient stays polite.
 */
export function announce(message: string, level: Politeness = "polite") {
  const el = ensureNode(level);
  if (!el) return;
  // Clear then set so the same string re-announces.
  el.textContent = "";
  if (setTimers[level]) window.clearTimeout(setTimers[level]);
  setTimers[level] = window.setTimeout(() => {
    if (el) el.textContent = message;
  }, 30);
  if (clearTimers[level]) window.clearTimeout(clearTimers[level]);
  clearTimers[level] = window.setTimeout(() => {
    if (el) el.textContent = "";
  }, 4000);
}
